import React, { useState, useEffect, useRef } from 'react'
import {
  Bell,
  BellOff,
  ChevronLeft,
  ChevronRight,
  Edit2,
  Eye,
  Heart,
  Lock,
  MessageCircle,
  MoreHorizontal,
  Share2,
  Shield,
  ShieldCheck,
  Star,
  User,
  Users,
  Zap,
  Gift,
  Calendar,
  Settings,
  Sliders,
  Camera,
  Sparkles,
  Trophy,
  ArrowRight,
  PlusCircle,
  FileText,
} from 'lucide-react'
interface ProfileTab {
  id: string
  label: string
  icon: string
  badge?: number | boolean
  premiumOnly?: boolean
}
interface Post {
  id: string
  type: 'text' | 'image' | 'video' | 'poll' | 'product'
  content: string
  media?: string[]
  likes: number
  comments: number
  isPremium: boolean
  isLocked: boolean
  timestamp: string
  author: {
    name: string
    username: string
    avatar: string
    isVerified: boolean
  }
}
interface UserData {
  id: string
  displayName: string
  username: string
  avatar: string
  coverImage: string
  tagline: string
  role: 'standard' | 'premium' | 'creator' | 'admin'
  isVerified: boolean
  joinDate: string
  followers: number
  following: number
  intimacyXP: number
  streakDays: number
  fanClubActive: boolean
  posts: Post[]
}
interface FanClubData {
  memberCount: number
  price: number
  benefits: string[]
  isSubscribed: boolean
}
interface TwitterStyleProfileProps {
  isOwnProfile?: boolean
  userData?: Partial<UserData>
  fanClubData?: FanClubData
  onBack?: () => void
  onEditProfile?: () => void
  onOpenSettings?: () => void
}
export const TwitterStyleProfile: React.FC<TwitterStyleProfileProps> = ({
  isOwnProfile = true,
  userData,
  fanClubData,
  onBack,
  onEditProfile,
  onOpenSettings,
}) => {
  // Default user data (fallback values)
  const defaultUserData: UserData = {
    id: '1',
    displayName: 'Mitch',
    username: 'mitch',
    avatar:
      'https://uploadthingy.s3.us-west-1.amazonaws.com/7HKmwMUzjW6BcbyATvuJB7/blue_male_eyes.jpg',
    coverImage:
      'https://uploadthingy.s3.us-west-1.amazonaws.com/r888EaKjsu1oUw83JmaxQo/profile_banner_fire.jpg',
    tagline:
      'AI enthusiast. Looking for meaningful connections in a digital world. SinShift VIP member.',
    role: 'standard',
    isVerified: true,
    joinDate: 'January 2022',
    followers: 1250,
    following: 432,
    intimacyXP: 345,
    streakDays: 7,
    fanClubActive: true,
    posts: [],
  }
  const defaultFanClubData: FanClubData = {
    memberCount: 152,
    price: 9.99,
    benefits: [
      'Exclusive daily content',
      'Private messaging',
      'Custom requests',
      'Behind the scenes access',
    ],
    isSubscribed: false,
  }
  // Merge provided data with defaults
  const user = { ...defaultUserData, ...userData }
  const fanClub = fanClubData || defaultFanClubData
  // State
  const [isFollowing, setIsFollowing] = useState(false)
  const [activeTab, setActiveTab] = useState('for-you')
  const [showNotificationOptions, setShowNotificationOptions] = useState(false)
  const [notificationEnabled, setNotificationEnabled] = useState(false)
  const [viewMode, setViewMode] = useState<'own' | 'public'>(
    isOwnProfile ? 'own' : 'public',
  )
  const [showFanClubModal, setShowFanClubModal] = useState(false)
  const [showMoreDropdown, setShowMoreDropdown] = useState(false)
  const [showEditTagline, setShowEditTagline] = useState(false)
  const [tagline, setTagline] = useState(user.tagline)
  const [earnedXP, setEarnedXP] = useState<number | null>(null)
  // Refs for scroll containers
  const tabsRef = useRef<HTMLDivElement>(null)
  // Tabs configuration - single row with all filters
  const tabs: ProfileTab[] = [
    { id: 'for-you', label: 'For You', icon: '🔥' },
    { id: 'daily-targets', label: 'Daily Targets', icon: '🎯', badge: 3 },
    { id: 'kinks', label: 'Kinks', icon: '🔞', premiumOnly: true },
    { id: 'polls', label: 'Polls', icon: '📊', badge: 2 },
    {
      id: 'fan-club',
      label: 'Fan Club',
      icon: '💎',
      premiumOnly: user.role !== 'premium' && user.role !== 'creator',
    },
    { id: 'posts', label: 'Posts', icon: '📝' },
    { id: 'photos', label: 'Photos', icon: '📸' },
    { id: 'vr-ar', label: 'VR/AR', icon: '🎮', premiumOnly: true },
    { id: 'likes', label: 'Likes', icon: '❤️' },
  ]
  // Sample posts for the feed
  const samplePosts: Post[] = [
    {
      id: '1',
      type: 'image',
      content: 'Check out my latest photoshoot! 📸✨ #NewContent',
      media: [
        'https://uploadthingy.s3.us-west-1.amazonaws.com/mJgakxWK7KjMTjzBL7C4xf/487511292_10212349614692967_1221945836348771543_n.jpg',
      ],
      likes: 245,
      comments: 32,
      isPremium: false,
      isLocked: false,
      timestamp: '2h ago',
      author: {
        name: user.displayName,
        username: user.username,
        avatar: user.avatar,
        isVerified: user.isVerified,
      },
    },
    {
      id: '2',
      type: 'text',
      content:
        "Just posted some exclusive content for my premium fans! Check it out if you're subscribed 💋",
      media: [],
      likes: 189,
      comments: 27,
      isPremium: true,
      isLocked: true,
      timestamp: '5h ago',
      author: {
        name: user.displayName,
        username: user.username,
        avatar: user.avatar,
        isVerified: user.isVerified,
      },
    },
    {
      id: '3',
      type: 'poll',
      content: 'What should I post next? Vote now!',
      media: [],
      likes: 312,
      comments: 45,
      isPremium: false,
      isLocked: false,
      timestamp: '1d ago',
      author: {
        name: user.displayName,
        username: user.username,
        avatar: user.avatar,
        isVerified: user.isVerified,
      },
    },
  ]
  // Hot angels for empty state recommendation
  const hotAngels = [
    {
      id: '1',
      name: 'GoldenShowerTina',
      username: 'GoldenShowerTina',
      avatar:
        'https://uploadthingy.s3.us-west-1.amazonaws.com/bU73WvXeTZLjt1PKcu9KyE/image.png',
      isOnline: true,
    },
    {
      id: '2',
      name: 'Bonny Blue',
      username: 'BonnyBlueOfficial',
      avatar:
        'https://uploadthingy.s3.us-west-1.amazonaws.com/jq4Dkth7vgkmZJ8GZmzWDP/bonnyblue.png',
      isOnline: false,
    },
    {
      id: '3',
      name: 'Kim Holland',
      username: 'KimHolland',
      avatar:
        'https://uploadthingy.s3.us-west-1.amazonaws.com/bZ3iVXv5nZEMY6F847weq5/kimholland.png',
      isOnline: true,
    },
  ]
  // Daily targets data
  const dailyTargets = [
    {
      id: 1,
      title: 'Complete Questionnaire',
      description: 'Tell us about your preferences',
      icon: 'clipboard',
      reward: 200,
      progress: 0,
      total: 1,
      isNew: true,
      color: 'green',
    },
    {
      id: 2,
      title: 'Like 5 Posts',
      description: 'Show some love to your favorite angels',
      icon: 'heart',
      reward: 15,
      progress: 2,
      total: 5,
      isNew: false,
      color: 'pink',
    },
    {
      id: 3,
      title: 'Send a Gift',
      description: "Make your favorite angel's day",
      icon: 'gift',
      reward: 10,
      progress: 0,
      total: 1,
      isNew: false,
      color: 'purple',
    },
  ]
  // Fan club recommendations
  const fanClubRecommendations = [
    {
      id: '1',
      name: 'GoldenShowerTina',
      username: 'GoldenShowerTina',
      avatar:
        'https://uploadthingy.s3.us-west-1.amazonaws.com/bU73WvXeTZLjt1PKcu9KyE/image.png',
      price: 9.99,
      memberCount: 2450,
      description: 'Your sinful Angel, always ready to play... 💋',
      tags: ['Exclusive Photos', 'Private Chat', 'Custom Videos'],
    },
    {
      id: '2',
      name: 'Bonny Blue',
      username: 'BonnyBlueOfficial',
      avatar:
        'https://uploadthingy.s3.us-west-1.amazonaws.com/jq4Dkth7vgkmZJ8GZmzWDP/bonnyblue.png',
      price: 12.99,
      memberCount: 3210,
      description: 'Dominant & playful, here to fulfill your desires 😈',
      tags: ['Daily Content', 'Live Streams', 'Voice Messages'],
    },
  ]
  // Simulate XP earned notification on mount
  useEffect(() => {
    const timer = setTimeout(() => {
      setEarnedXP(25)
      // Auto-hide after 5 seconds
      const hideTimer = setTimeout(() => {
        setEarnedXP(null)
      }, 5000)
      return () => clearTimeout(hideTimer)
    }, 2000)
    return () => clearTimeout(timer)
  }, [])
  // Handler functions
  const toggleFollow = () => {
    setIsFollowing(!isFollowing)
  }
  const toggleNotification = () => {
    setNotificationEnabled(!notificationEnabled)
    setShowNotificationOptions(false)
  }
  const handleTabScroll = (direction: 'left' | 'right') => {
    if (tabsRef.current) {
      const scrollAmount = 200
      if (direction === 'left') {
        tabsRef.current.scrollLeft -= scrollAmount
      } else {
        tabsRef.current.scrollLeft += scrollAmount
      }
    }
  }
  const toggleViewMode = () => {
    setViewMode(viewMode === 'own' ? 'public' : 'own')
  }
  const handleSaveTagline = () => {
    setShowEditTagline(false)
    // In a real app, you would save this to the backend
  }
  // Determine if the profile has content based on active tab
  const hasContent =
    activeTab === 'daily-targets' ||
    activeTab === 'fan-club' ||
    samplePosts.length > 0
  return (
    <div className="w-full flex flex-col bg-white dark:bg-gray-950 text-gray-900 dark:text-white min-h-screen relative">
      {/* XP Earned Notification */}
      {earnedXP !== null && (
        <div className="fixed top-20 right-4 z-50 animate-in slide-in-from-top-5 duration-300">
          <div className="bg-gradient-to-r from-purple-500/90 to-pink-500/90 backdrop-blur-sm text-white px-4 py-3 rounded-lg shadow-lg flex items-center gap-3">
            <div className="p-2 bg-white/20 rounded-full">
              <Sparkles size={18} className="text-yellow-300" />
            </div>
            <div>
              <p className="font-medium">+{earnedXP} Intimacy XP Earned!</p>
              <p className="text-xs text-white/80">
                For voting in today's poll
              </p>
            </div>
            <button
              className="ml-2 p-1 hover:bg-white/20 rounded-full transition-colors"
              onClick={() => setEarnedXP(null)}
            >
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      )}
      {/* Profile Header */}
      <div className="relative">
        {/* Cover Image */}
        <div
          className="h-40 md:h-52 w-full bg-cover bg-center"
          style={{
            backgroundImage: `url('${user.coverImage}')`,
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 to-black/10"></div>
          {/* Cover image edit button (only on own profile) */}
          {viewMode === 'own' && (
            <button
              className="absolute bottom-3 right-3 p-2 bg-black/40 hover:bg-black/60 text-white rounded-full transition-colors"
              title="Change cover image"
            >
              <Camera size={16} />
            </button>
          )}
        </div>
        {/* Profile Info Container */}
        <div className="px-4 pb-4 pt-16 bg-white dark:bg-gray-900 relative">
          {/* Avatar (positioned to overlap cover image) */}
          <div className="absolute -top-16 left-6">
            <div className="w-32 h-32 rounded-full border-4 border-white dark:border-gray-900 overflow-hidden shadow-lg">
              <img
                src={user.avatar}
                alt={user.displayName}
                className="w-full h-full object-cover"
              />
              {/* Avatar edit button (only on own profile) */}
              {viewMode === 'own' && (
                <div className="absolute inset-0 bg-black/0 hover:bg-black/30 flex items-center justify-center transition-colors group">
                  <button
                    className="p-2 bg-black/70 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                    title="Change profile picture"
                  >
                    <Camera size={16} />
                  </button>
                </div>
              )}
            </div>
          </div>
          {/* Action Buttons (top right) */}
          <div className="flex justify-end gap-2 mb-4">
            {viewMode === 'own' ? (
              <>
                <button
                  onClick={toggleViewMode}
                  className="p-2 bg-gray-100 dark:bg-gray-800 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                  title="View as public"
                >
                  <Eye size={20} className="text-gray-700 dark:text-gray-300" />
                </button>
                <button
                  onClick={onEditProfile}
                  className="p-2 bg-gray-100 dark:bg-gray-800 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                  title="Edit profile"
                >
                  <Edit2
                    size={20}
                    className="text-gray-700 dark:text-gray-300"
                  />
                </button>
                <button
                  onClick={onOpenSettings}
                  className="p-2 bg-gray-100 dark:bg-gray-800 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                  title="Settings"
                >
                  <Settings
                    size={20}
                    className="text-gray-700 dark:text-gray-300"
                  />
                </button>
              </>
            ) : (
              <>
                <div className="relative">
                  <button
                    className="p-2 bg-gray-100 dark:bg-gray-800 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                    onClick={() =>
                      setShowNotificationOptions(!showNotificationOptions)
                    }
                    title={
                      notificationEnabled
                        ? 'Notifications on'
                        : 'Notifications off'
                    }
                  >
                    {notificationEnabled ? (
                      <Bell
                        size={20}
                        className="text-pink-500 dark:text-pink-400"
                      />
                    ) : (
                      <BellOff
                        size={20}
                        className="text-gray-700 dark:text-gray-300"
                      />
                    )}
                  </button>
                  {showNotificationOptions && (
                    <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-10 animate-in fade-in duration-200">
                      <button
                        className="w-full px-4 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700 rounded-t-lg flex items-center gap-2"
                        onClick={toggleNotification}
                      >
                        {notificationEnabled ? (
                          <>
                            <BellOff
                              size={16}
                              className="text-gray-700 dark:text-gray-300"
                            />
                            <span>Disable notifications</span>
                          </>
                        ) : (
                          <>
                            <Bell
                              size={16}
                              className="text-pink-500 dark:text-pink-400"
                            />
                            <span>Enable notifications</span>
                          </>
                        )}
                      </button>
                    </div>
                  )}
                </div>
                <button
                  className="p-2 bg-gray-100 dark:bg-gray-800 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                  title="Share profile"
                >
                  <Share2
                    size={20}
                    className="text-gray-700 dark:text-gray-300"
                  />
                </button>
                <div className="relative">
                  <button
                    className="p-2 bg-gray-100 dark:bg-gray-800 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                    onClick={() => setShowMoreDropdown(!showMoreDropdown)}
                    title="More options"
                  >
                    <MoreHorizontal
                      size={20}
                      className="text-gray-700 dark:text-gray-300"
                    />
                  </button>
                  {showMoreDropdown && (
                    <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-10 animate-in fade-in duration-200">
                      <button className="w-full px-4 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2 text-red-500 dark:text-red-400">
                        <Shield size={16} />
                        <span>Report profile</span>
                      </button>
                      <button className="w-full px-4 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700 rounded-b-lg flex items-center gap-2 text-red-500 dark:text-red-400">
                        <Lock size={16} />
                        <span>Block user</span>
                      </button>
                    </div>
                  )}
                </div>
                <button
                  className={`px-4 py-1.5 rounded-md font-medium text-sm transition-colors ${
                    isFollowing
                      ? 'bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-white hover:bg-red-100 dark:hover:bg-red-900/30 hover:text-red-600 dark:hover:text-red-400'
                      : 'bg-gradient-to-r from-pink-500 to-purple-500 text-white hover:from-pink-600 hover:to-purple-600'
                  }`}
                  onClick={toggleFollow}
                >
                  {isFollowing ? 'Following' : 'Follow'}
                </button>
              </>
            )}
          </div>
          {/* User Info */}
          <div className="mt-2 pl-40 md:pl-36 -mt-10 md:-mt-12">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-xl font-bold">{user.displayName}</h2>
              {user.isVerified && (
                <span className="text-blue-500 dark:text-blue-400">
                  <ShieldCheck size={16} className="inline" />
                </span>
              )}
              {user.role === 'premium' && (
                <span className="bg-gradient-to-r from-pink-500/20 to-purple-500/20 text-pink-600 dark:text-pink-400 px-2 py-0.5 rounded-full text-xs font-medium">
                  Premium
                </span>
              )}
              {user.role === 'creator' && (
                <span className="bg-gradient-to-r from-purple-500/20 to-blue-500/20 text-purple-600 dark:text-purple-400 px-2 py-0.5 rounded-full text-xs font-medium">
                  Creator
                </span>
              )}
            </div>
            <p className="text-gray-600 dark:text-gray-400">@{user.username}</p>
            {/* Tagline - editable on own profile */}
            {showEditTagline && viewMode === 'own' ? (
              <div className="mt-3 flex items-center gap-2">
                <input
                  type="text"
                  value={tagline}
                  onChange={(e) => setTagline(e.target.value)}
                  className="flex-1 px-3 py-1.5 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-md text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-pink-500 dark:focus:ring-pink-400"
                  placeholder="Add your tagline..."
                  maxLength={150}
                />
                <button
                  onClick={handleSaveTagline}
                  className="px-3 py-1.5 bg-pink-500 hover:bg-pink-600 text-white rounded-md text-sm transition-colors"
                >
                  Save
                </button>
              </div>
            ) : (
              <div className="mt-3 group relative">
                <p className="text-gray-800 dark:text-gray-200 text-sm pr-8">
                  {tagline}
                </p>
                {viewMode === 'own' && (
                  <button
                    onClick={() => setShowEditTagline(true)}
                    className="absolute right-0 top-0 opacity-0 group-hover:opacity-100 p-1 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-opacity"
                    title="Edit tagline"
                  >
                    <Edit2 size={14} />
                  </button>
                )}
              </div>
            )}
            {/* User Stats */}
            <div className="flex flex-wrap gap-y-2 text-sm text-gray-600 dark:text-gray-400 mt-3">
              <div className="flex items-center mr-4">
                <Calendar className="mr-1" size={16} />
                <span>Joined {user.joinDate}</span>
              </div>
              {user.streakDays > 0 && (
                <div className="flex items-center mr-4">
                  <Flame className="mr-1 text-orange-500" size={16} />
                  <span>{user.streakDays} day streak</span>
                </div>
              )}
              {user.fanClubActive && (
                <div className="flex items-center mr-4">
                  <Star className="mr-1 text-yellow-500" size={16} />
                  <span>Fan Club Active</span>
                </div>
              )}
              {user.intimacyXP > 0 && (
                <div className="flex items-center mr-4">
                  <Zap className="mr-1 text-purple-500" size={16} />
                  <span>{user.intimacyXP} Intimacy XP</span>
                </div>
              )}
            </div>
            {/* Followers/Following */}
            <div className="flex gap-4 mt-3">
              <div className="flex items-center gap-1">
                <span className="font-medium">
                  {user.following.toLocaleString()}
                </span>
                <span className="text-gray-600 dark:text-gray-400">
                  Following
                </span>
              </div>
              <div className="flex items-center gap-1">
                <span className="font-medium">
                  {user.followers.toLocaleString()}
                </span>
                <span className="text-gray-600 dark:text-gray-400">
                  {user.followers === 1 ? 'Follower' : 'Followers'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Single Filter Row - Tabs Navigation */}
      <div className="sticky top-0 z-10 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 shadow-sm">
        <div className="relative">
          <div
            className="flex overflow-x-auto scrollbar-hide py-1 px-1"
            ref={tabsRef}
          >
            {tabs.map((tab) => (
              <button
                key={tab.id}
                className={`px-4 py-3 font-medium flex items-center gap-2 whitespace-nowrap relative ${
                  activeTab === tab.id
                    ? 'text-pink-600 dark:text-pink-400 border-b-2 border-pink-500 dark:border-pink-400'
                    : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
                } ${tab.premiumOnly ? 'opacity-70' : ''}`}
                onClick={() => setActiveTab(tab.id)}
                disabled={tab.premiumOnly}
              >
                <span>{tab.icon}</span>
                <span>{tab.label}</span>
                {tab.badge && (
                  <span className="absolute -top-1 -right-1 bg-pink-500 text-white text-xs w-5 h-5 flex items-center justify-center rounded-full">
                    {typeof tab.badge === 'number' ? tab.badge : ''}
                  </span>
                )}
                {tab.premiumOnly && (
                  <span className="absolute -top-1 -right-1 bg-gray-800 text-white text-xs p-1 rounded-full">
                    <Lock size={10} />
                  </span>
                )}
              </button>
            ))}
          </div>
          {/* Scroll Arrows */}
          <button
            className="absolute left-0 top-1/2 transform -translate-y-1/2 bg-white/80 dark:bg-gray-900/80 backdrop-blur-sm p-1 rounded-r-md shadow-md text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
            onClick={() => handleTabScroll('left')}
          >
            <ChevronLeft size={16} />
          </button>
          <button
            className="absolute right-0 top-1/2 transform -translate-y-1/2 bg-white/80 dark:bg-gray-900/80 backdrop-blur-sm p-1 rounded-l-md shadow-md text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
            onClick={() => handleTabScroll('right')}
          >
            <ChevronRight size={16} />
          </button>
        </div>
      </div>
      {/* Content Area */}
      <div className="p-4 bg-gray-50 dark:bg-gray-950 flex-1">
        {/* Angel Promotion - Added here */}
        <div className="mb-4 p-3 bg-gradient-to-r from-pink-50 to-purple-50 dark:from-pink-900/20 dark:to-purple-900/20 rounded-lg border border-pink-100 dark:border-pink-800/30 animate-pulse-subtle">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full overflow-hidden flex-shrink-0 border-2 border-pink-200 dark:border-pink-700">
              <img
                src="https://uploadthingy.s3.us-west-1.amazonaws.com/cWevg3yWQUraz6TkDYX6Gk/image.png"
                alt="My Angel"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="flex-1">
              <div className="flex items-center">
                <h3 className="font-medium text-gray-900 dark:text-white">
                  Check out my latest Angel created by this awesome new Unleash
                  feature!
                </h3>
                <span className="ml-2 bg-pink-500 text-white text-xs px-1.5 py-0.5 rounded-full">
                  New
                </span>
              </div>
              <p className="text-sm text-gray-700 dark:text-gray-300 mt-1">
                She's absolutely perfect and has become my favorite! 💕
              </p>
              <div className="flex items-center gap-2 mt-2">
                <button
                  onClick={() => {
                    // Dispatch event to navigate to unleash
                    const event = new CustomEvent('navigate', {
                      detail: {
                        section: 'unleash',
                      },
                    })
                    document.dispatchEvent(event)
                  }}
                  className="px-3 py-1 bg-gradient-to-r from-pink-600 to-purple-600 text-white text-xs rounded-full hover:shadow-md hover:shadow-pink-500/20 transition-all"
                >
                  Try Unleash
                </button>
                <button className="px-3 py-1 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 text-xs rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 transition-all">
                  View Profile
                </button>
              </div>
            </div>
          </div>
        </div>
        {activeTab === 'fan-club' ? (
          <FanClubSection
            fanClub={fanClub}
            user={user}
            recommendations={fanClubRecommendations}
          />
        ) : activeTab === 'daily-targets' ? (
          <DailyTargetsSection targets={dailyTargets} />
        ) : hasContent ? (
          <ContentFeed posts={samplePosts} activeTab={activeTab} user={user} />
        ) : (
          <EmptyState
            username={user.username}
            activeTab={activeTab}
            hotAngels={hotAngels}
          />
        )}
      </div>
      {/* Daily Rewards Banner (only shown for own profile) */}
      {viewMode === 'own' && (
        <div className="mt-auto bg-gradient-to-r from-pink-500/10 to-purple-500/10 dark:from-pink-500/20 dark:to-purple-500/20 border border-pink-200 dark:border-pink-900/30 rounded-xl m-4 p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-gradient-to-r from-pink-500 to-purple-500 flex items-center justify-center text-white text-xl">
                🎁
              </div>
              <div>
                <h3 className="font-bold text-gray-900 dark:text-white">
                  Daily Rewards
                </h3>
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  Complete daily actions to earn tokens and XP!
                </p>
              </div>
            </div>
            <button
              className="px-4 py-2 bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 text-white rounded-lg text-sm font-medium shadow-md shadow-pink-500/20 transition-all"
              onClick={() => setActiveTab('daily-targets')}
            >
              Claim Rewards
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
// Helper Components
const EmptyState: React.FC<{
  username: string
  activeTab: string
  hotAngels: any[]
}> = ({ username, activeTab, hotAngels }) => {
  return (
    <div className="py-8 flex flex-col items-center justify-center text-center">
      <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mb-4">
        {activeTab === 'for-you' ? (
          <span className="text-2xl">🔥</span>
        ) : activeTab === 'daily-targets' ? (
          <span className="text-2xl">🎯</span>
        ) : activeTab === 'kinks' ? (
          <span className="text-2xl">🔞</span>
        ) : activeTab === 'polls' ? (
          <span className="text-2xl">📊</span>
        ) : activeTab === 'posts' ? (
          <span className="text-2xl">📝</span>
        ) : activeTab === 'photos' ? (
          <span className="text-2xl">📸</span>
        ) : activeTab === 'vr-ar' ? (
          <span className="text-2xl">🎮</span>
        ) : (
          <Heart size={24} className="text-gray-500 dark:text-gray-400" />
        )}
      </div>
      <h3 className="text-lg font-bold mb-2 text-gray-900 dark:text-white">
        No content yet
      </h3>
      <p className="text-gray-600 dark:text-gray-400 max-w-md mb-8">
        When {username} posts new {activeTab.replace(/-/g, ' ')}, you'll see
        them here.
      </p>
      {/* Hot Angels Section */}
      <div className="w-full max-w-xl mx-auto mb-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-lg text-gray-900 dark:text-white">
            Hot Angels 🔥
          </h3>
          <button className="text-sm text-pink-600 dark:text-pink-400">
            See All
          </button>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {hotAngels.map((angel) => (
            <div key={angel.id} className="flex flex-col items-center">
              <div className="relative">
                <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full overflow-hidden">
                  <img
                    src={angel.avatar}
                    alt={angel.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                {angel.isOnline && (
                  <div className="absolute bottom-0 right-0 w-3 h-3 sm:w-4 sm:h-4 bg-green-500 rounded-full border-2 border-white dark:border-gray-950"></div>
                )}
              </div>
              <p className="mt-2 font-medium text-sm text-gray-900 dark:text-white">
                {angel.name}
              </p>
              <button className="mt-1 px-3 py-1 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-md text-xs text-gray-900 dark:text-gray-200">
                Follow
              </button>
            </div>
          ))}
        </div>
      </div>
      {/* What's Hot Section */}
      <div className="w-full max-w-xl mx-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-lg text-gray-900 dark:text-white">
            What's Hot 🔥
          </h3>
          <button className="text-sm text-pink-600 dark:text-pink-400">
            See More
          </button>
        </div>
        <div className="space-y-4">
          {[
            {
              id: 1,
              title: 'Beach Photoshoot Collection',
              image:
                'https://uploadthingy.s3.us-west-1.amazonaws.com/mJgakxWK7KjMTjzBL7C4xf/487511292_10212349614692967_1221945836348771543_n.jpg',
              likes: 1.2,
              comments: 320,
              author: 'Mia',
            },
            {
              id: 2,
              title: 'Lingerie Haul - New Collection',
              image:
                'https://uploadthingy.s3.us-west-1.amazonaws.com/mJgakxWK7KjMTjzBL7C4xf/487511292_10212349614692967_1221945836348771543_n.jpg',
              likes: 2.4,
              comments: 567,
              author: 'Riley',
            },
          ].map((content) => (
            <div
              key={content.id}
              className="bg-white dark:bg-gray-900 rounded-md overflow-hidden border border-gray-200 dark:border-gray-800 shadow-sm"
            >
              <div className="aspect-video overflow-hidden">
                <img
                  src={content.image}
                  alt={content.title}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-3">
                <div className="flex justify-between items-start">
                  <h4 className="font-medium text-gray-900 dark:text-white">
                    {content.title}
                  </h4>
                  <div className="flex items-center text-pink-600 dark:text-pink-400">
                    <Heart size={14} fill="currentColor" />
                    <span className="ml-1 text-xs">{content.likes}k</span>
                  </div>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  By {content.author} • {content.comments} comments
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
const ContentFeed: React.FC<{
  posts: Post[]
  activeTab: string
  user: Partial<UserData>
}> = ({ posts, activeTab, user }) => {
  // Filter posts based on active tab
  const filteredPosts = posts.filter((post) => {
    if (activeTab === 'for-you') return true
    if (activeTab === 'photos')
      return post.type === 'image' || post.type === 'video'
    if (activeTab === 'posts') return post.type === 'text'
    if (activeTab === 'polls') return post.type === 'poll'
    return true
  })
  return (
    <div className="space-y-4">
      {/* Activity Summary Card */}
      <div className="bg-white dark:bg-gray-900 rounded-lg shadow-sm border border-gray-200 dark:border-gray-800 p-4">
        <h3 className="font-medium text-gray-900 dark:text-white mb-3">
          Your Activity
        </h3>
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-purple-100 dark:bg-purple-900/30 rounded-full flex items-center justify-center text-purple-600 dark:text-purple-400">
                <Heart size={16} />
              </div>
              <div>
                <p className="text-sm text-gray-900 dark:text-white">
                  You liked 3 posts today
                </p>
                <p className="text-xs text-gray-600 dark:text-gray-400">
                  2 more to complete your daily target
                </p>
              </div>
            </div>
            <span className="text-xs bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 px-2 py-0.5 rounded-full">
              +5 XP
            </span>
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-pink-100 dark:bg-pink-900/30 rounded-full flex items-center justify-center text-pink-600 dark:text-pink-400">
                <MessageCircle size={16} />
              </div>
              <div>
                <p className="text-sm text-gray-900 dark:text-white">
                  You commented on GoldenShowerTina's post
                </p>
                <p className="text-xs text-gray-600 dark:text-gray-400">
                  1 hour ago
                </p>
              </div>
            </div>
            <span className="text-xs bg-pink-100 dark:bg-pink-900/30 text-pink-600 dark:text-pink-400 px-2 py-0.5 rounded-full">
              +3 XP
            </span>
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center text-blue-600 dark:text-blue-400">
                <Trophy size={16} />
              </div>
              <div>
                <p className="text-sm text-gray-900 dark:text-white">
                  You earned the "Early Bird" badge
                </p>
                <p className="text-xs text-gray-600 dark:text-gray-400">
                  Today at 7:30 AM
                </p>
              </div>
            </div>
            <span className="text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 px-2 py-0.5 rounded-full">
              +10 XP
            </span>
          </div>
        </div>
        <div className="mt-4 pt-3 border-t border-gray-200 dark:border-gray-800">
          <button className="w-full py-2 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg text-sm font-medium text-gray-900 dark:text-white transition-colors flex items-center justify-center gap-1">
            <span>View All Activity</span>
            <ArrowRight size={14} />
          </button>
        </div>
      </div>
      {/* Recommended Content */}
      <div className="bg-white dark:bg-gray-900 rounded-lg shadow-sm border border-gray-200 dark:border-gray-800 p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-medium text-gray-900 dark:text-white">
            Recommended For You
          </h3>
          <button className="text-xs text-pink-600 dark:text-pink-400">
            Refresh
          </button>
        </div>
        <div className="space-y-3">
          {filteredPosts.map((post) => (
            <div
              key={post.id}
              className={`bg-white dark:bg-gray-900 rounded-lg shadow-sm border ${
                post.isPremium
                  ? 'border-pink-200 dark:border-pink-900/30'
                  : 'border-gray-200 dark:border-gray-800'
              } overflow-hidden`}
            >
              {/* Post Header */}
              <div className="p-3 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full overflow-hidden">
                    <img
                      src={post.author.avatar}
                      alt={post.author.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div>
                    <div className="flex items-center gap-1">
                      <h4 className="font-medium text-gray-900 dark:text-white">
                        {post.author.name}
                      </h4>
                      {post.author.isVerified && (
                        <ShieldCheck size={14} className="text-blue-500" />
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <p className="text-xs text-gray-600 dark:text-gray-400">
                        @{post.author.username}
                      </p>
                      <span className="text-xs text-gray-500 dark:text-gray-500">
                        •
                      </span>
                      <p className="text-xs text-gray-600 dark:text-gray-400">
                        {post.timestamp}
                      </p>
                    </div>
                  </div>
                </div>
                {post.isPremium && (
                  <span className="px-2 py-1 bg-gradient-to-r from-pink-500/10 to-purple-500/10 dark:from-pink-500/20 dark:to-purple-500/20 text-pink-600 dark:text-pink-400 rounded-full text-xs font-medium">
                    Premium
                  </span>
                )}
              </div>
              {/* Post Content */}
              <div className="px-3 pb-2">
                <p className="text-gray-900 dark:text-white mb-3">
                  {post.content}
                </p>
                {/* Media Content */}
                {post.type === 'image' &&
                  post.media &&
                  post.media.length > 0 && (
                    <div
                      className={`relative rounded-lg overflow-hidden ${post.isLocked ? 'blur-lg' : ''}`}
                    >
                      <img
                        src={post.media[0]}
                        alt="Post media"
                        className="w-full h-auto rounded-lg"
                      />
                      {post.isLocked && (
                        <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/50 backdrop-blur-md">
                          <Lock size={24} className="text-white mb-2" />
                          <p className="text-white font-medium mb-2">
                            Premium Content
                          </p>
                          <button className="px-4 py-2 bg-gradient-to-r from-pink-500 to-purple-500 text-white rounded-lg text-sm">
                            Unlock Now
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                {/* Poll Content */}
                {post.type === 'poll' && (
                  <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 mt-2 mb-3">
                    <h4 className="font-medium text-gray-900 dark:text-white mb-2">
                      Poll: What should I post next?
                    </h4>
                    <div className="space-y-2">
                      {[
                        'Bikini photos',
                        'Workout video',
                        'Q&A session',
                        'Behind the scenes',
                      ].map((option, index) => (
                        <div
                          key={index}
                          className="relative h-10 bg-white dark:bg-gray-700 rounded-md overflow-hidden border border-gray-200 dark:border-gray-600"
                        >
                          <div
                            className="absolute left-0 top-0 bottom-0 bg-gradient-to-r from-pink-500/20 to-purple-500/20"
                            style={{ width: `${[70, 45, 30, 20][index]}%` }}
                          ></div>
                          <div className="absolute inset-0 flex items-center justify-between px-3">
                            <span className="text-gray-900 dark:text-white text-sm">
                              {option}
                            </span>
                            <span className="text-gray-700 dark:text-gray-300 text-sm font-medium">
                              {[70, 45, 30, 20][index]}%
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                    <p className="text-xs text-gray-600 dark:text-gray-400 mt-2">
                      245 votes • 2 days left
                    </p>
                  </div>
                )}
              </div>
              {/* Post Actions */}
              <div className="px-3 py-2 border-t border-gray-100 dark:border-gray-800 flex items-center justify-between">
                <div className="flex items-center gap-6">
                  <button className="flex items-center gap-1 text-gray-600 dark:text-gray-400 hover:text-pink-500 dark:hover:text-pink-400">
                    <Heart size={18} />
                    <span className="text-sm">{post.likes}</span>
                  </button>
                  <button className="flex items-center gap-1 text-gray-600 dark:text-gray-400 hover:text-blue-500 dark:hover:text-blue-400">
                    <MessageCircle size={18} />
                    <span className="text-sm">{post.comments}</span>
                  </button>
                </div>
                <button className="flex items-center gap-1 text-gray-600 dark:text-gray-400 hover:text-purple-500 dark:hover:text-purple-400">
                  <Gift size={18} />
                  <span className="text-sm">Send Gift</span>
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
const DailyTargetsSection: React.FC<{
  targets: {
    id: number
    title: string
    description: string
    icon: string
    reward: number
    progress: number
    total: number
    isNew: boolean
    color: string
  }[]
}> = ({ targets }) => {
  return (
    <div className="space-y-4">
      {/* Streak Card */}
      <div className="bg-gradient-to-r from-orange-500/10 to-red-500/10 dark:from-orange-500/20 dark:to-red-500/20 rounded-lg border border-orange-200 dark:border-orange-900/30 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-gradient-to-r from-orange-500 to-red-500 flex items-center justify-center text-white text-xl">
              🔥
            </div>
            <div>
              <h3 className="font-bold text-gray-900 dark:text-white">
                7 Day Streak!
              </h3>
              <p className="text-sm text-gray-700 dark:text-gray-300">
                Keep it up to earn bonus rewards
              </p>
            </div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-orange-600 dark:text-orange-400">
              +50
            </div>
            <div className="text-xs text-orange-600 dark:text-orange-400">
              Tokens
            </div>
          </div>
        </div>
        {/* Streak Calendar */}
        <div className="mt-4 flex items-center justify-between">
          {[1, 2, 3, 4, 5, 6, 7].map((day) => (
            <div
              key={day}
              className={`w-10 h-10 flex items-center justify-center rounded-full ${
                day <= 7
                  ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white'
                  : 'bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-600'
              }`}
            >
              {day}
            </div>
          ))}
        </div>
      </div>
      {/* Daily Targets */}
      <div className="bg-white dark:bg-gray-900 rounded-lg shadow-sm border border-gray-200 dark:border-gray-800 p-4">
        <h3 className="font-medium text-gray-900 dark:text-white mb-4 flex items-center gap-2">
          <Trophy size={18} className="text-yellow-500" />
          <span>Daily Targets</span>
          <span className="ml-auto text-sm text-gray-600 dark:text-gray-400">
            2/5 completed
          </span>
        </h3>
        <div className="space-y-4">
          {targets.map((target) => (
            <div
              key={target.id}
              className={`p-3 bg-gradient-to-r from-${target.color}-900/10 to-${target.color === 'pink' ? 'purple' : target.color === 'purple' ? 'blue' : target.color === 'green' ? 'teal' : target.color}-900/10 dark:from-${target.color}-900/20 dark:to-${target.color === 'pink' ? 'purple' : target.color === 'purple' ? 'blue' : target.color === 'green' ? 'teal' : target.color}-900/20 rounded-lg border border-${target.color}-200/20 dark:border-${target.color}-800/30 ${target.isNew ? 'animate-pulse-subtle' : ''}`}
            >
              <div className="flex items-center gap-3">
                <div
                  className={`w-9 h-9 flex items-center justify-center bg-${target.color}-500/10 dark:bg-${target.color}-500/20 rounded-full text-${target.color}-500 flex-shrink-0`}
                >
                  {target.icon === 'clipboard' && <FileText size={18} />}
                  {target.icon === 'heart' && <Heart size={18} />}
                  {target.icon === 'gift' && <Gift size={18} />}
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <h3 className="font-medium text-sm flex items-center gap-1">
                      {target.title}
                      {target.isNew && (
                        <span
                          className={`bg-${target.color}-500 text-white text-[10px] px-1.5 py-0.5 rounded-full`}
                        >
                          NEW
                        </span>
                      )}
                    </h3>
                    <div
                      className={`text-xs text-${target.color}-500 font-semibold`}
                    >
                      {target.reward} 💗
                    </div>
                  </div>
                  <p className="text-xs text-gray-700 dark:text-gray-300 mt-1">
                    {target.description}
                  </p>
                  <div className="mt-2">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        Progress
                      </span>
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {target.progress}/{target.total}
                      </span>
                    </div>
                    <div className="h-1.5 bg-gray-200 dark:bg-gray-800 rounded-full overflow-hidden">
                      <div
                        className={`h-full bg-gradient-to-r from-${target.color}-500 to-${target.color === 'pink' ? 'purple' : target.color === 'purple' ? 'blue' : target.color === 'green' ? 'teal' : target.color}-500 rounded-full`}
                        style={{
                          width: `${(target.progress / target.total) * 100}%`,
                        }}
                      ></div>
                    </div>
                  </div>
                  {target.progress < target.total && (
                    <button
                      className={`mt-3 w-full py-1.5 bg-gradient-to-r from-${target.color}-600 to-${target.color === 'pink' ? 'purple' : target.color === 'purple' ? 'blue' : target.color === 'green' ? 'teal' : target.color}-600 rounded-full text-white text-xs font-medium hover:shadow-lg hover:shadow-${target.color}-500/20 transition-all duration-300`}
                    >
                      {target.progress === 0 ? 'Start Now' : 'Continue'}
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-4 pt-3 border-t border-gray-200 dark:border-gray-800 flex justify-between">
          <div className="text-sm">
            <span className="text-gray-700 dark:text-gray-300">
              Total rewards available:
            </span>
            <span className="ml-1 font-semibold text-pink-600 dark:text-pink-400">
              320 💗
            </span>
          </div>
          <button className="text-sm text-pink-600 dark:text-pink-400 font-medium flex items-center gap-1">
            <span>View All Targets</span>
            <ArrowRight size={14} />
          </button>
        </div>
      </div>
      {/* Achievements Preview */}
      <div className="bg-white dark:bg-gray-900 rounded-lg shadow-sm border border-gray-200 dark:border-gray-800 p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-medium text-gray-900 dark:text-white flex items-center gap-2">
            <Trophy size={18} className="text-yellow-500" />
            <span>Achievements</span>
          </h3>
          <button className="text-sm text-pink-600 dark:text-pink-400">
            View All
          </button>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {[
            { name: 'Early Bird', icon: '🌅', progress: 100, unlocked: true },
            {
              name: 'Generous Gifter',
              icon: '🎁',
              progress: 60,
              unlocked: false,
            },
            {
              name: 'Conversation Starter',
              icon: '💬',
              progress: 80,
              unlocked: false,
            },
          ].map((achievement, index) => (
            <div
              key={index}
              className="flex flex-col items-center p-3 bg-gray-50 dark:bg-gray-800 rounded-lg"
            >
              <div
                className={`w-12 h-12 rounded-full flex items-center justify-center text-xl ${achievement.unlocked ? 'bg-gradient-to-r from-yellow-400 to-orange-500' : 'bg-gray-200 dark:bg-gray-700'}`}
              >
                {achievement.icon}
              </div>
              <p className="mt-2 text-sm font-medium text-gray-900 dark:text-white text-center">
                {achievement.name}
              </p>
              {!achievement.unlocked && (
                <div className="w-full mt-2">
                  <div className="h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-yellow-400 to-orange-500 rounded-full"
                      style={{ width: `${achievement.progress}%` }}
                    ></div>
                  </div>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400 text-center">
                    {achievement.progress}%
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
const FanClubSection: React.FC<{
  fanClub: FanClubData
  user: Partial<UserData>
  recommendations: any[]
}> = ({ fanClub, user, recommendations }) => {
  return (
    <div className="space-y-4">
      {/* Active Fan Clubs */}
      <div className="bg-white dark:bg-gray-900 rounded-lg shadow-sm border border-gray-200 dark:border-gray-800 p-4">
        <h3 className="font-medium text-gray-900 dark:text-white mb-4 flex items-center gap-2">
          <Star size={18} className="text-yellow-500" />
          <span>Your Fan Club Memberships</span>
        </h3>
        {user.fanClubActive ? (
          <div className="bg-gradient-to-r from-pink-500/10 to-purple-500/10 dark:from-pink-500/20 dark:to-purple-500/20 rounded-lg p-4 mb-4">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full overflow-hidden border-2 border-pink-200 dark:border-pink-900/30">
                <img
                  src="https://uploadthingy.s3.us-west-1.amazonaws.com/bU73WvXeTZLjt1PKcu9KyE/image.png"
                  alt="GoldenShowerTina"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between">
                  <h4 className="font-medium text-gray-900 dark:text-white flex items-center gap-1">
                    GoldenShowerTina's Fan Club
                    <ShieldCheck size={14} className="text-blue-500" />
                  </h4>
                  <span className="text-xs text-pink-600 dark:text-pink-400 font-medium">
                    $9.99/month
                  </span>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  Membership renews in 18 days
                </p>
                <div className="mt-2 flex items-center gap-2">
                  <span className="text-xs bg-pink-100 dark:bg-pink-900/30 text-pink-600 dark:text-pink-400 px-2 py-0.5 rounded-full">
                    Exclusive Photos
                  </span>
                  <span className="text-xs bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 px-2 py-0.5 rounded-full">
                    Private Chat
                  </span>
                </div>
              </div>
            </div>
            <div className="mt-3 flex justify-between">
              <div>
                <span className="text-xs text-gray-600 dark:text-gray-400">
                  New content:{' '}
                </span>
                <span className="text-xs font-medium text-green-600 dark:text-green-400">
                  3 new posts today
                </span>
              </div>
              <button className="text-sm text-pink-600 dark:text-pink-400 font-medium">
                View Content
              </button>
            </div>
          </div>
        ) : (
          <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-6 text-center">
            <div className="w-16 h-16 mx-auto bg-gray-200 dark:bg-gray-700 rounded-full flex items-center justify-center mb-3">
              <Star size={24} className="text-gray-400 dark:text-gray-500" />
            </div>
            <h4 className="font-medium text-gray-900 dark:text-white mb-2">
              No Fan Club Memberships
            </h4>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Join fan clubs to get exclusive content and perks from your
              favorite creators
            </p>
            <button className="px-4 py-2 bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 text-white rounded-lg text-sm font-medium shadow-md shadow-pink-500/20 transition-all">
              Explore Fan Clubs
            </button>
          </div>
        )}
      </div>
      {/* Recommended Fan Clubs */}
      <div className="bg-white dark:bg-gray-900 rounded-lg shadow-sm border border-gray-200 dark:border-gray-800 p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-medium text-gray-900 dark:text-white flex items-center gap-2">
            <Sparkles size={18} className="text-yellow-500" />
            <span>Recommended Fan Clubs</span>
          </h3>
          <button className="text-sm text-pink-600 dark:text-pink-400">
            View All
          </button>
        </div>
        <div className="space-y-4">
          {recommendations.map((club) => (
            <div
              key={club.id}
              className="bg-gradient-to-r from-pink-500/5 to-purple-500/5 dark:from-pink-500/10 dark:to-purple-500/10 rounded-lg border border-pink-100 dark:border-pink-900/20 overflow-hidden"
            >
              <div className="p-4">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-full overflow-hidden border-2 border-pink-200 dark:border-pink-900/30">
                    <img
                      src={club.avatar}
                      alt={club.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <h4 className="font-medium text-gray-900 dark:text-white flex items-center gap-1">
                        {club.name}'s Fan Club
                        <ShieldCheck size={14} className="text-blue-500" />
                      </h4>
                      <span className="text-xs text-pink-600 dark:text-pink-400 font-medium">
                        ${club.price}/month
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                      {club.description}
                    </p>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {club.tags.map((tag, index) => (
                        <span
                          key={index}
                          className={`text-xs ${
                            index % 3 === 0
                              ? 'bg-pink-100 dark:bg-pink-900/30 text-pink-600 dark:text-pink-400'
                              : index % 3 === 1
                                ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400'
                                : 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
                          } px-2 py-0.5 rounded-full`}
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-white dark:bg-gray-900 p-3 border-t border-pink-100 dark:border-pink-900/20 flex items-center justify-between">
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  <span>{club.memberCount.toLocaleString()} members</span>
                  <span className="mx-2">•</span>
                  <span>Posts daily</span>
                </div>
                <button className="px-3 py-1.5 bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 text-white rounded-md text-sm font-medium transition-all">
                  Join Fan Club
                </button>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-4 text-center">
          <button className="inline-flex items-center gap-1 text-sm text-pink-600 dark:text-pink-400 font-medium">
            <PlusCircle size={16} />
            <span>Discover More Fan Clubs</span>
          </button>
        </div>
      </div>
      {/* Fan Club Benefits */}
      <div className="bg-white dark:bg-gray-900 rounded-lg shadow-sm border border-gray-200 dark:border-gray-800 p-4">
        <h3 className="font-medium text-gray-900 dark:text-white mb-4">
          Fan Club Benefits
        </h3>
        <div className="space-y-3">
          {[
            {
              icon: '📸',
              title: 'Exclusive Content',
              description:
                'Access photos and videos not available to regular followers',
            },
            {
              icon: '💬',
              title: 'Private Messaging',
              description: 'Direct access to chat with your favorite creators',
            },
            {
              icon: '🎁',
              title: 'Custom Requests',
              description: 'Request personalized content just for you',
            },
            {
              icon: '🔔',
              title: 'Priority Notifications',
              description: 'Be the first to know when new content is posted',
            },
          ].map((benefit, index) => (
            <div key={index} className="flex items-start gap-3">
              <div className="w-8 h-8 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center text-lg flex-shrink-0">
                {benefit.icon}
              </div>
              <div>
                <h4 className="font-medium text-gray-900 dark:text-white text-sm">
                  {benefit.title}
                </h4>
                <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">
                  {benefit.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
// Helper components
const Flame = ({ className, size }: { className?: string; size?: number }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size || 24}
    height={size || 24}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z" />
  </svg>
)
const FileText = ({
  className,
  size,
}: {
  className?: string
  size?: number
}) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size || 24}
    height={size || 24}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="16" y1="13" x2="8" y2="13" />
    <line x1="16" y1="17" x2="8" y2="17" />
    <line x1="10" y1="9" x2="8" y2="9" />
  </svg>
)
